import type { ChatComposerOpenAction } from '@ui/ChatComposer';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppBarStyles } from '@client/style/style.constants';
import {
  MainLayout,
  PanelTabSlide,
  SidePanel,
  usePanelTabSlideDirection,
} from '@ui/layout';
import { IconButton } from '@ui/buttons';
import { PillTabs } from '@ui/PillTabs';
import { Tooltip } from '@ui/Tooltip';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { ELEMENT_LIST_HEADER_HEIGHT } from '@features/proposals/ElementListRow/element-list.constants';
import { Design } from '@features/design';
import {
  useDesignRightPanelResizeHandle,
  useCollapseDesignRightPanel,
  useDesignRightPanelWidth,
  useExpandDesignRightPanel,
} from '@features/design/design-right-panel-resize.hook';
import { DESIGN_AGENT_DOCK_HEIGHT_PROPERTY } from '@features/design/components/DesignAiChatTestPanel';
import { useDesignCommentsUnread } from '@features/design/components/use-design-comments-unread.hook';
import {
  discardBuildingCandidate,
  useBlueprintInteractionMode,
  useBuildingCandidate,
  useDesignRightPanelTab,
  useSetDesignRightPanelTab,
} from '@features/design/store/design-store.hooks';
import {
  useDeselectBuilding,
  useSyncDesignStore,
} from '@features/design/hooks/storeys.hooks';
import { useBlueprintUnderlays } from '@features/design/hooks/design.hooks';
import { usePageNavigationHotkeys } from '@client/hooks/use-page-navigation-hotkeys.hook';
import { useIsSharedProjectRoute } from '@client/hooks/use-router.hooks';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { usePropertiesPanelRequestId } from '@features/design/store/design-store.hooks';
import { useDesignUrlState } from '@features/design/hooks/design-url-state.hooks';
import { shouldDeselectBuildingFromLeftPanel } from './design-view-panel.helpers';

const RIGHT_PANEL_TABS = ['agent', 'properties'] as const;
const DESIGN_PANEL_TAB_ORDER = ['agent', 'properties', 'comments'] as const;
type RightPanelTab = (typeof RIGHT_PANEL_TABS)[number];
type RightPanelTabHandler = (tab: RightPanelTab) => void;
const COLLAPSED_TAB_VALUE: readonly RightPanelTab[] = [];
type DesignRightPanel = RightPanelTab | 'comments';

const useStyles = makeStyles()(({ palette }) => ({
  rightPanelTabs: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    height: ELEMENT_LIST_HEADER_HEIGHT,
    paddingLeft: 8,
    backgroundColor: palette.warmSurface.main,
  },
  rightPanelTabsStuck: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  commentsButton: {
    display: 'flex',
    alignItems: 'center',
    height: 22,
    marginLeft: 'auto',
    marginRight: 8,
    borderRadius: 5,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
    '& .MuiIconButton-root': {
      width: 26,
      height: 22,
      padding: '0 2px',
      borderRadius: 5,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  commentsButtonActive: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    color: 'rgba(0, 0, 0, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    '& .MuiIconButton-root, & .MuiIconButton-root:hover': {
      color: 'inherit',
    },
  },
  commentsButtonLabel: {
    paddingRight: 8,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    color: 'inherit',
    '&.MuiTypography-root': {
      margin: 0,
    },
  },
  collapsedCommentsButton: {
    display: 'flex',
    borderRadius: 5,
    '& .MuiIconButton-root': {
      width: 26,
      height: 22,
      padding: '0 2px',
    },
  },
  rightPanelBody: {
    paddingBottom: `var(${DESIGN_AGENT_DOCK_HEIGHT_PROPERTY}, 0px)`,
  },
  rightPanelOverlay: {
    position: 'fixed',
    top: AppBarStyles.HEIGHT,
    right: 0,
    bottom: 0,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    backgroundColor: palette.warmSurface.main,
    borderLeft: `1px solid ${palette.divider}`,
  },
  rightPanelOverlayCollapsed: {
    backgroundColor: 'transparent',
    borderLeft: 'none',
    pointerEvents: 'none',
  },
  rightPanelResizeHandle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: -4,
    zIndex: 3,
    width: 8,
    cursor: 'ew-resize',
    pointerEvents: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  collapsedTabs: {
    position: 'fixed',
    // North mark is top 14 and 32px tall, so its centre is 30px into the map.
    // The panel is 32px tall (22px tabs, 8px padding, 2px border), so the gap
    // above it is 14px. The right edge uses that same gap.
    top: AppBarStyles.HEIGHT + 30,
    right: 14,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    padding: 4,
    transform: 'translateY(-50%)',
    backgroundColor: palette.warmSurface.main,
    backgroundImage:
      'linear-gradient(rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.03))',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    boxShadow: '0px 1px 13px 0px rgba(0, 0, 0, 0.1)',
  },
  collapsedComposer: {
    position: 'fixed',
    right: 14,
    bottom: 14,
    zIndex: 2,
    width: 320,
  },
}));

const renderRightPanelBody = (
  tab: DesignRightPanel,
  showCalibrationPanel: boolean,
  contentClassName: string,
  onDockMessageSent: RightPanelTabHandler,
  onOpenConversation: (action?: ChatComposerOpenAction) => void,
  openingAction: ChatComposerOpenAction | undefined,
) => {
  switch (tab) {
    case 'comments':
      return <Design.Comments />;
    case 'properties':
      return (
        <>
          <SidePanel.Content>
            <Box className={contentClassName}>
              {showCalibrationPanel ? (
                <Design.BlueprintCalibration />
              ) : (
                <>
                  <Design.Results />
                  <Design.Activities />
                </>
              )}
            </Box>
          </SidePanel.Content>
          <Design.AiChatTest
            onMessageSent={() => onDockMessageSent('agent')}
            onOpenConversation={onOpenConversation}
          />
        </>
      );
    case 'agent':
      return (
        <Design.AiChatTest placement="panel" openingAction={openingAction} />
      );
    default:
      return assertUnreachable(tab);
  }
};

const DesignView: React.FC = () => {
  const { classes, cx } = useStyles();
  const rightPanelWidth = useDesignRightPanelWidth();
  const onRightPanelResize = useDesignRightPanelResizeHandle();
  const collapseRightPanel = useCollapseDesignRightPanel();
  const expandRightPanel = useExpandDesignRightPanel();
  const collapsedTabsRef = useRef<HTMLDivElement>(null);
  const [collapsedTabsInset, setCollapsedTabsInset] = useState(0);
  const rightPanelTabsRef = useRef<HTMLDivElement>(null);
  const [headerStuck, setHeaderStuck] = useState(false);
  const isSharedProject = useIsSharedProjectRoute();
  const storedRightPanelTab = useDesignRightPanelTab();
  const rightPanelTab: DesignRightPanel =
    isSharedProject &&
    (storedRightPanelTab === 'agent' || storedRightPanelTab === 'comments')
      ? 'properties'
      : storedRightPanelTab;
  const rightPanelTabs: readonly RightPanelTab[] = isSharedProject
    ? ['properties']
    : RIGHT_PANEL_TABS;
  const slideDirection = usePanelTabSlideDirection(
    DESIGN_PANEL_TAB_ORDER,
    rightPanelTab,
  );
  const setRightPanelTab = useSetDesignRightPanelTab();
  useDesignUrlState();
  useSyncDesignStore();
  const deselectBuilding = useDeselectBuilding();
  const buildingCandidate = useBuildingCandidate();
  usePageNavigationHotkeys(['b', 'r']);
  const blueprintMode = useBlueprintInteractionMode();
  const blueprintUnderlays = useBlueprintUnderlays();
  // PDF panel while the underlay is selected (move/transform or set-scale/calibrate).
  const showCalibrationPanel =
    blueprintUnderlays.length > 0 &&
    (blueprintMode === 'transform' || blueprintMode === 'calibrate');

  useEffect(
    () => () => {
      discardBuildingCandidate();
    },
    [],
  );

  const onRightPanelTabChange = useCallback<RightPanelTabHandler>(
    (tab) => {
      setRightPanelTab(tab);
    },
    [setRightPanelTab],
  );

  const onCommentsClick = useCallback(() => {
    setRightPanelTab('comments');
  }, [setRightPanelTab]);

  const hasUnreadComments = useDesignCommentsUnread(
    rightPanelTab === 'comments',
  );

  const onCollapsedTabChange = useCallback<RightPanelTabHandler>(
    (tab) => {
      setRightPanelTab(tab);
      expandRightPanel();
    },
    [expandRightPanel, setRightPanelTab],
  );

  const onCollapsedCommentsClick = useCallback(() => {
    setRightPanelTab('comments');
    expandRightPanel();
  }, [expandRightPanel, setRightPanelTab]);

  const [openingAction, setOpeningAction] = useState<ChatComposerOpenAction>();
  useEffect(() => {
    if (rightPanelTab !== 'agent' || rightPanelWidth === 0)
      setOpeningAction(undefined);
  }, [rightPanelTab, rightPanelWidth]);
  const onOpenDockConversation = useCallback(
    (action?: ChatComposerOpenAction) => {
      setOpeningAction(action);
      setRightPanelTab('agent');
    },
    [setRightPanelTab],
  );
  const onOpenFloatingConversation = useCallback(
    (action?: ChatComposerOpenAction) => {
      setOpeningAction(action);
      setRightPanelTab('agent');
      expandRightPanel();
    },
    [setRightPanelTab, expandRightPanel],
  );

  const onFloatingMessageSent = useCallback(() => {
    setRightPanelTab('agent');
    expandRightPanel();
  }, [expandRightPanel, setRightPanelTab]);

  useLayoutEffect(() => {
    const node = collapsedTabsRef.current;
    if (!node) {
      setCollapsedTabsInset(0);
      return;
    }
    const measure = () => {
      const next = Math.ceil(
        window.innerWidth - node.getBoundingClientRect().left,
      );
      setCollapsedTabsInset((current) => (current === next ? current : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [rightPanelWidth]);

  const propertiesPanelRequestId = usePropertiesPanelRequestId();
  const previousPropertiesPanelRequestId = usePrevious(
    propertiesPanelRequestId,
  );

  useEffect(() => {
    if (previousPropertiesPanelRequestId === undefined) return;
    if (previousPropertiesPanelRequestId === propertiesPanelRequestId) return;
    setRightPanelTab('properties');
  }, [
    previousPropertiesPanelRequestId,
    propertiesPanelRequestId,
    setRightPanelTab,
  ]);

  useEffect(() => {
    const scrollRoot = rightPanelTabsRef.current?.closest('aside');
    if (!scrollRoot) return;
    const onScroll = () => setHeaderStuck(scrollRoot.scrollTop > 0);
    onScroll();
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollRoot.removeEventListener('scroll', onScroll);
  }, []);

  const handleLeftPanelClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // Only the unused panel canvas is a background click. Child controls and
      // rows retain their own interaction semantics. New building is an active
      // creation flow and only its explicit Back action may leave the panel.
      if (
        shouldDeselectBuildingFromLeftPanel({
          clickedPanelBackground: event.target === event.currentTarget,
          hasBuildingCandidate: Boolean(buildingCandidate),
        })
      ) {
        deselectBuilding();
      }
    },
    [buildingCandidate, deselectBuilding],
  );

  return (
    <MainLayout
      panelLeft={
        <SidePanel
          aria-label="Storeys"
          onClick={handleLeftPanelClick}
          sx={{
            backgroundColor: (theme) => theme.palette.warmSurface.main,
            borderRight: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <SidePanel.Content>
            <Design.Storeys />
          </SidePanel.Content>
        </SidePanel>
      }
      panelRight={null}
    >
      <MainLayout.Content>
        <Design.Map3d controlsRightInset={collapsedTabsInset} fullViewport />
        <Design.ApplyToAllPagesDialog />
        {rightPanelWidth === 0 && (
          <Box ref={collapsedTabsRef} className={classes.collapsedTabs}>
            <PillTabs
              tabs={rightPanelTabs}
              value={COLLAPSED_TAB_VALUE}
              onChange={onCollapsedTabChange}
            >
              {!isSharedProject && (
                <Box className={classes.collapsedCommentsButton}>
                  <Tooltip
                    title={hasUnreadComments ? 'New activities' : 'History'}
                    placement="bottom"
                  >
                    <IconButton
                      category="ui"
                      icon={hasUnreadComments ? 'commentUnread' : 'comment'}
                      aria-label="History"
                      onClick={onCollapsedCommentsClick}
                    />
                  </Tooltip>
                </Box>
              )}
            </PillTabs>
          </Box>
        )}
        {rightPanelWidth === 0 && !isSharedProject && (
          <Box className={classes.collapsedComposer} data-design-exit-panel="">
            <Design.AiChatTest
              floating
              onMessageSent={onFloatingMessageSent}
              onOpenConversation={onOpenFloatingConversation}
            />
          </Box>
        )}
        <Box
          data-design-exit-panel=""
          className={cx(
            classes.rightPanelOverlay,
            rightPanelWidth === 0 && classes.rightPanelOverlayCollapsed,
          )}
          style={{ width: rightPanelWidth }}
        >
          <Box
            className={classes.rightPanelResizeHandle}
            onMouseDown={onRightPanelResize}
            onDoubleClick={collapseRightPanel}
            data-design-right-panel-resize=""
          />
          <SidePanel aria-label="Results" width={rightPanelWidth}>
            {rightPanelWidth > 0 && (
              <>
                <Box
                  ref={rightPanelTabsRef}
                  className={cx(
                    classes.rightPanelTabs,
                    headerStuck && classes.rightPanelTabsStuck,
                  )}
                >
                  <PillTabs
                    tabs={rightPanelTabs}
                    value={
                      rightPanelTab === 'comments'
                        ? COLLAPSED_TAB_VALUE
                        : rightPanelTab
                    }
                    onChange={onRightPanelTabChange}
                  />
                  {!isSharedProject && (
                    <Box
                      className={cx(
                        classes.commentsButton,
                        rightPanelTab === 'comments' &&
                          classes.commentsButtonActive,
                      )}
                    >
                      <Tooltip
                        title={hasUnreadComments ? 'New activities' : 'History'}
                        placement="bottom"
                      >
                        <IconButton
                          category="ui"
                          icon={hasUnreadComments ? 'commentUnread' : 'comment'}
                          aria-label="History"
                          onClick={onCommentsClick}
                        />
                      </Tooltip>
                      {rightPanelTab === 'comments' && (
                        <Typography
                          className={classes.commentsButtonLabel}
                          component="span"
                          onClick={onCommentsClick}
                        >
                          History
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <PanelTabSlide
                  slideKey={rightPanelTab}
                  fill={rightPanelTab !== 'properties'}
                  direction={slideDirection}
                >
                  {renderRightPanelBody(
                    rightPanelTab,
                    showCalibrationPanel,
                    classes.rightPanelBody,
                    onRightPanelTabChange,
                    onOpenDockConversation,
                    openingAction,
                  )}
                </PanelTabSlide>
              </>
            )}
          </SidePanel>
        </Box>
      </MainLayout.Content>
    </MainLayout>
  );
};

export default DesignView;
